import { useMemo } from 'react';
import type { MeuEncontro } from '../types';

interface Props {
  encontros: MeuEncontro[];
  cidade: string;
}

function formatarProxima(iso: string): string {
  const d = new Date(iso);
  const semana = d.toLocaleDateString('pt-BR', { weekday: 'long' });
  const dia = d.getDate();
  const mes = d.toLocaleDateString('pt-BR', { month: 'long' });
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${semana}, ${dia} de ${mes}, ${hora}`;
}

function formatarSimples(iso: string): string {
  const d = new Date(iso);
  const data = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${data} · ${hora}`;
}

function icsTimestamp(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}T${p(d.getHours())}${p(
    d.getMinutes(),
  )}00`;
}

function linkMapa(local: string | null, cidade: string): string {
  const q = [local, cidade].filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

export default function SuasDatas({ encontros, cidade }: Props) {
  const { futuras, passadas } = useMemo(() => {
    const f = encontros.filter((e) => !e.ja_passou);
    const p = encontros.filter((e) => e.ja_passou);
    return { futuras: f, passadas: p };
  }, [encontros]);

  if (encontros.length === 0) return null;

  const proxima = futuras[0] ?? null;
  const outras = futuras.slice(1);

  function baixarICS() {
    const linhas = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Se Liga Catolico//PT',
      'CALSCALE:GREGORIAN',
    ];
    encontros.forEach((e, i) => {
      const inicio = new Date(e.inicio_em);
      const fim = e.fim_em ? new Date(e.fim_em) : new Date(inicio.getTime() + 3600 * 1000);
      linhas.push(
        'BEGIN:VEVENT',
        `UID:${e.encontro_id}-${i}@seligacatolico`,
        `DTSTAMP:${icsTimestamp(new Date())}`,
        `DTSTART:${icsTimestamp(inicio)}`,
        `DTEND:${icsTimestamp(fim)}`,
        `SUMMARY:${e.nome.replace(/,/g, '\\,')}`,
        ...(e.local_nome ? [`LOCATION:${e.local_nome.replace(/,/g, '\\,')}`] : []),
        'END:VEVENT',
      );
    });
    linhas.push('END:VCALENDAR');

    const blob = new Blob([linhas.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'minhas-datas.ics';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <section className="mt-6 overflow-hidden rounded-lg border border-secondary-200 bg-white">
      <div className="border-b border-secondary-100 px-6 py-4">
        <h2 className="text-lg font-medium text-foreground-950">Suas datas</h2>
      </div>

      <div className="px-6 py-5">
        {proxima && (
          <div className="rounded-lg bg-primary-100 px-5 py-4">
            <span className="text-[11px] font-bold uppercase tracking-wider text-primary-700">
              Próxima
            </span>
            <p className="mt-1 text-lg font-bold text-primary-900">{proxima.nome}</p>
            <p className="mt-1 text-sm text-primary-800">{formatarProxima(proxima.inicio_em)}</p>
            {proxima.local_nome && (
              <p className="mt-1 text-sm text-primary-800">{proxima.local_nome}</p>
            )}
            <p className="mt-1 text-sm text-primary-800">
              Presença {proxima.obrigatorio ? 'obrigatória' : 'facultativa'}
            </p>
            <a
              href={linkMapa(proxima.local_nome, cidade)}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-map-pin-line" />
              </span>
              Ver mapa
            </a>
          </div>
        )}

        {(outras.length > 0 || passadas.length > 0) && (
          <div className="mt-4 divide-y divide-secondary-100">
            {outras.map((e) => (
              <div key={e.encontro_id} className="flex items-start gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-medium text-foreground-900">{e.nome}</p>
                  <p className="mt-0.5 text-sm text-foreground-600">
                    {formatarSimples(e.inicio_em)}
                    {e.local_nome ? ` · ${e.local_nome}` : ''}
                  </p>
                </div>
              </div>
            ))}
            {passadas.map((e) => (
              <div key={e.encontro_id} className="flex items-start gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-medium text-foreground-400">{e.nome}</p>
                  <p className="mt-0.5 text-sm text-foreground-400">
                    {formatarSimples(e.inicio_em)}
                    {e.local_nome ? ` · ${e.local_nome}` : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={baixarICS}
          className="mt-4 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm font-medium text-foreground-600 transition-colors hover:text-foreground-900"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-calendar-line" />
          </span>
          Adicionar ao meu calendário
        </button>
      </div>
    </section>
  );
}